import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  NewFolderModal_CreateGroupLibraryFolder,
  NewFolderModal_CreateGroupLibraryFolderVariables,
} from './graphql-types/NewFolderModal_CreateGroupLibraryFolder';

const ADD_FOLDER = gql`
  mutation NewFolderModal_CreateGroupLibraryFolder(
    $input: CreateGroupLibraryFolderInput!
  ) {
    createGroupLibraryFolder(input: $input)
  }
`;

export const NewFolderModal: React.FC<{
  onClose: () => void;
  isOpen: boolean;
  groupId: string;
  parentLibraryFolderId: string;
}> = ({ onClose, isOpen, groupId, parentLibraryFolderId }) => {
  const toast = useToast();
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      folderName: '',
    },
  });

  const [createFolder, { loading }] = useMutation<
    NewFolderModal_CreateGroupLibraryFolder,
    NewFolderModal_CreateGroupLibraryFolderVariables
  >(ADD_FOLDER, {
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
    awaitRefetchQueries: true,
  });

  const onSubmit = handleSubmit(async (data) => {
    if (!groupId || !parentLibraryFolderId) {
      return;
    }

    try {
      await createFolder({
        variables: {
          input: {
            groupId,
            name: data.folderName,
            parentLibraryFolderId,
          },
        },
      });
      handleClose();
    } catch (error) {
      toast({
        title: 'Error while creating the folder, please try again',
        status: 'error',
      });
    }
  });
  const handleClose = () => {
    reset(); // Reset form fields and errors
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Folder</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired isInvalid={!!errors.folderName}>
            <FormLabel>Folder Name</FormLabel>

            <Controller
              control={control}
              name="folderName"
              render={({ field: { value, onChange, ref } }) => (
                <Input value={value} onChange={onChange} ref={ref} autoFocus />
              )}
              rules={{
                validate: {
                  required: (value) =>
                    value.trim().length > 0 ||
                    `The folder name cannot be empty`,
                },
              }}
            />
            <FormErrorMessage>{errors.folderName?.message}</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button
              onClick={handleClose}
              variant="outline"
              colorScheme="gray"
              isDisabled={loading}
            >
              Cancel
            </Button>

            <Button type="submit" isLoading={loading} onClick={onSubmit}>
              Create
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
